import {
  FC, useEffect, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import useCurrency from 'hooks/useCurreny';
import Select from 'components/Select';
import MaskInput from 'components/MaskedInput';
import Input from 'components/Input';
import { SelectProps } from 'utils/types';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import { formatSwissNumber } from 'utils/formatNumber';

interface Props {
  productType: string;
  setProductType: (value: string) => void;
  productSelect: SelectProps[];
  nv: string;
  setNv: (value: string) => void;
  mt: string;
  setMt: (value: string) => void;
  getProductStatus: string;
  errorNv: string | null;
  errorMt: string | null;
  surface: string;
  setSurface: (value: string) => void;
  getMtha: string | number;
  getEuroHa: string | number;
  getErrorOfSurface: string | null;
  getProductMt: string | number;
  disabled: boolean;
}

const ProductCrop: FC<Props> = ({
  productType,
  setProductType,
  productSelect,
  nv,
  setNv,
  mt,
  setMt,
  getProductStatus,
  errorNv,
  errorMt,
  surface,
  setSurface,
  getMtha,
  getEuroHa,
  getProductMt,
  getErrorOfSurface,
  disabled,
}) => {
  const { t } = useTranslation();
  const { currencyGreen } = useCurrency();
  const { onlyMt, mtHa, onlyAc } = useCroptimizer();
  const [productValue, setProductValue] = useState<{value: string; label: string}[]>([]);

  useEffect(() => {
    if (productType) {
      setProductValue([{
        value: productType,
        label: productType,
      }]);

      return;
    }

    setProductValue([]);
  }, [productType]);

  return (
    <div className="flex flex-col">
      <div>
        <div className="font-bold text-lg mb-8">{t('product')}</div>
        <div className="flex sm:flex-wrap mb-8 mx-[-0.5rem]">
          <div className="w-3/12 sm:w-full px-2 sm:mb-8">
            <Select
              options={productSelect}
              onChange={(value) => setProductType(value.value)}
              title={t('name')}
              value={productValue}
              disabled={disabled}
            />
          </div>
          <div className="w-9/12 sm:w-full flex sm:flex-wrap mx-[-0.5rem] px-2 box-content">
            <div className='w-1/5 sm:w-1/3 sm:min-w-auto sm px-2 sm:mb-6'>
              <MaskInput
                titleClassName={`${
                  disabled ? 'bg-transparent' : 'bg-white'
                } top-[-9px] left-[8px]`}
                title={t('nv')}
                className="w-full"
                value={nv}
                onChange={setNv}
                redBorder={!!errorNv}
                disabled={disabled}
              />
              <div className="text-[#FF3B30] text-xs ml-1 mt-1 h-2">
                {errorNv && <div> {errorNv}</div>}
              </div>
            </div>
            <div className='w-1/5 sm:w-1/3 sm:min-w-auto sm px-2 sm:mb-6'>
              <MaskInput
                titleClassName={`${
                  disabled ? 'bg-transparent' : 'bg-white'
                } top-[-9px] left-[8px]`}
                title={t('price')}
                className="w-full"
                value={mt}
                onChange={setMt}
                redBorder={!!errorMt}
                disabled={disabled}
              />
              <div className="text-[#FF3B30] text-xs ml-1 mt-1 h-2">
                {errorMt && <div> {errorMt}</div>}
              </div>
            </div>
            <div className='w-1/5 sm:w-1/3 sm:min-w-auto sm px-2'>
              <MaskInput
                titleClassName={'bg-disable-gradient top-[-9px] left-[8px]'}
                title={mtHa}
                className="w-full"
                disabled
                value={formatSwissNumber(Number(getMtha))}
              />
            </div>
            <div className='w-1/5 sm:w-1/2 sm:min-w-auto sm px-2'>
              <Input
                titleClassName={'bg-disable-gradient top-[-9px] left-[8px]'}
                title={t('status')}
                className="w-full"
                value={getProductStatus}
                disabled
              />
            </div>
            <div className='w-1/5 sm:w-1/2 sm:min-w-auto sm px-2'>
              <MaskInput
                titleClassName={'bg-disable-gradient top-[-9px] left-[8px]'}
                title={`${currencyGreen}/${onlyAc}`}
                className="w-full"
                disabled
                value={formatSwissNumber(Number(getEuroHa))}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex mx-[-0.5rem]">
        <div className='w-1/2 px-2'>
          <MaskInput
            titleClassName={`${
              disabled ? 'bg-transparent' : 'bg-white'
            } top-[-9px] left-[8px]`}
            title={t('surface') + ` (${onlyAc})`}
            className="w-full"
            value={surface}
            onChange={setSurface}
            redBorder={!!getErrorOfSurface}
            disabled={disabled}
          />
          <div className="text-[#FF3B30] text-xs ml-1 mt-1 h-2">
            {getErrorOfSurface && <div> {getErrorOfSurface}</div>}
          </div>
        </div>
        <div className='w-1/2 px-2'>
          <MaskInput
            titleClassName={'bg-disable-gradient top-[-9px] left-[8px]'}
            title={t('product') + ` (${onlyMt})`}
            className="w-full"
            disabled
            value={formatSwissNumber(Number(getProductMt))}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductCrop;
